import React, { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Lightbulb, HelpCircle, Paperclip } from "lucide-react";
import { CategorySelect, checkFiles, uploadFiles, ScanNotice } from "./UploadDialog";
import { toHtml, token, LIMITS_TEXT, REMINDER, ALLOWED } from "./ideaUtils";
import { toast } from "sonner";

export function PostComposer({ org, cats, scanner, onClose, onDone }) {
  const [f, setF] = useState({ type: "idea", title: "", category_id: "", body: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const tok = useRef(token());
  const pick = (e) => { const err = checkFiles(e.target.files); if (err) { toast.error(err); e.target.value = ""; return; } setFiles(Array.from(e.target.files)); };
  const submit = async () => {
    if (!f.title.trim() || !f.body.trim() || !f.category_id) return toast.error("Add a title, category and details");
    setBusy(true);
    try {
      let ids = [];
      if (files.length) {
        const d = await uploadFiles({ org, category: f.category_id, files, clientToken: tok.current });
        d.results.filter((r) => !r.ok).forEach((r) => toast.error(`${r.name}: ${r.error}`));
        ids = d.results.filter((r) => r.ok && r.resource).map((r) => r.resource.id);
      }
      const { data } = await api.post("/ideas/posts", { organization_id: org, type: f.type, title: f.title, category_id: f.category_id, body_html: toHtml(f.body), resource_ids: ids, client_token: tok.current });
      toast.success("Posted"); onDone(data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const TypeBtn = ({ v, Icon, label }) => (
    <button type="button" onClick={() => setF({ ...f, type: v })} className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium flex items-center justify-center gap-2 ${f.type === v ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200"}`} data-testid={`ideas-type-${v}`}><Icon className="w-4 h-4" />{label}</button>);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-auto" data-testid="ideas-composer">
        <DialogHeader><DialogTitle>Share an Idea / Ask a Question</DialogTitle><DialogDescription>Visible to Agents and district staff in this group only.</DialogDescription></DialogHeader>
        <div className="flex gap-2"><TypeBtn v="idea" Icon={Lightbulb} label="Idea" /><TypeBtn v="question" Icon={HelpCircle} label="Question" /></div>
        <Input placeholder="Title" value={f.title} maxLength={200} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ideas-composer-title" />
        <CategorySelect cats={cats} value={f.category_id} onChange={(v) => setF({ ...f, category_id: v })} testId="ideas-category" />
        <Textarea rows={6} placeholder="Share the details. Use **bold** for emphasis and a blank line between paragraphs." value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="ideas-body" />
        <div>
          <label className="inline-flex items-center gap-1.5 text-sm text-[#006090] cursor-pointer"><Paperclip className="w-4 h-4" />Attach resources (optional)
            <input type="file" multiple className="sr-only" accept={ALLOWED.map((e) => `.${e}`).join(",")} onChange={pick} data-testid="ideas-attach-input" /></label>
          <p className="text-[11px] text-slate-500">{LIMITS_TEXT}. {REMINDER}</p>
          {files.length > 0 && <p className="text-xs text-slate-700 mt-1">{files.map((x) => x.name).join(", ")}</p>}
          {files.length > 0 && <ScanNotice configured={scanner} />}
        </div>
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-strong" data-testid="ideas-submit">{busy ? "Posting…" : "Post"}</Button></div>
      </DialogContent>
    </Dialog>
  );
}
